'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { customAxios } from '@/modules/common/api';
import { API } from '@/app/api/config';
import { normalizePhoneNumber } from '@/modules/common/phone';

export interface AuthUser {
  acId: number | string;
  nickname: string;
  phone: string;
  acEmail: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const config: AxiosRequestConfig & { skipAuthRedirect?: boolean } = { skipAuthRedirect: true };
      const res = await customAxios.get(`${API.USER_WEB}/info`, config);
      const info = res?.data?.response;
      if (info) {
        setUser({
          acId: info.id,
          nickname: info.acName,
          phone: normalizePhoneNumber(info.acPhone),
          acEmail: info.acEmail,
        });
      } else {
        setUser(null);
      }
    } catch (e) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const logout = useCallback(async () => {
    try {
      await customAxios.post(API.AUTHENTICATION_LOGOUT);
    } catch (e) {
      // 로그아웃은 실패해도 클라이언트 상태는 정리한다.
    } finally {
      setUser(null);
      window.location.href = '/';
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoggedIn: !!user, isLoading, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
